import { getAuth, onIdTokenChanged } from "firebase/auth";
import { api } from "./axios";
import { getIdToken } from "./firebase";

let chatbaseInitialized = false;
let chatbaseInitializing = false;
let tokenRefreshListenerRegistered = false;
// chatbase-auth is aggressively rate-limited (429). On load the initial auth,
// onIdTokenChanged firing on listener registration, and the 401-retry token
// refreshes all fire within the first few seconds — each with a *freshly
// minted* Firebase token (different `iat`), so they can't be de-duped by token
// equality. Throttle instead: at most one exchange per cooldown window. The
// identify token stays valid for ~1h, so using one a few seconds old is
// harmless, and this caps the request rate below the endpoint's limit.
let lastAuthedToken: string | null = null;
let lastAuthAttemptMs = 0;
const AUTH_COOLDOWN_MS = 15000;

export const initializeChatbase = async (): Promise<void> => {
	// Prevent duplicate initialization
	if (chatbaseInitialized) {
		console.log("Chatbase already initialized, skipping...");
		return;
	}

	// Prevent concurrent initialization attempts
	if (chatbaseInitializing) {
		console.log("Chatbase initialization already in progress, skipping...");
		return;
	}

	try {
		chatbaseInitializing = true;
		console.log("Starting Chatbase initialization...");

		// Initial setup
		const firebaseToken = await getIdToken();

		if (!firebaseToken) {
			console.error("No Firebase token available");
			chatbaseInitializing = false;
			return;
		}

		//Initial authorization
		await updateChatbaseAuth(firebaseToken);
		chatbaseInitialized = true;
		chatbaseInitializing = false;
		console.log("Chatbase initialization completed successfully");

		// Automatic token refresh listener
		setupTokenRefreshListener();
	} catch (error) {
		console.error("Failed to initialize Chatbase:", error);
		chatbaseInitializing = false;
	}
};

const updateChatbaseAuth = async (firebaseToken: string) => {
	// Skip if this exact token is already exchanged, or if we're still inside
	// the cooldown window after the last attempt (see the note above) — this is
	// what keeps the load-time burst of token refreshes from tripping the 429.
	if (firebaseToken === lastAuthedToken) {
		return;
	}
	if (Date.now() - lastAuthAttemptMs < AUTH_COOLDOWN_MS) {
		return;
	}
	lastAuthAttemptMs = Date.now();
	try {
		const response = await api.post("/auth/chatbase-auth", {
			firebaseToken: firebaseToken,
		});
		lastAuthedToken = firebaseToken;
		const { userId, hash, user } = response.data;

		if (window.chatbase) {
			window.chatbase("identify", {
				user_id: userId,
				user_hash: hash,
				user_metadata: {
					name: `${user.firstName} ${user.lastName}`.trim(),
					email: user.email,
					role: user.admin ? "Admin" : "User",
				},
			});
			console.log("Chatbase initialized for user:");
		} else {
			console.warn("Chatbase not loaded yet");
		}
	} catch (error) {
		console.error("Failed to update Chatbase auth:", error);
	}
};

//listen for token changes
const setupTokenRefreshListener = () => {
	// Register the Firebase listener at most once so token refreshes don't fan
	// out into multiple concurrent chatbase-auth POSTs.
	if (tokenRefreshListenerRegistered) {
		return;
	}
	tokenRefreshListenerRegistered = true;

	const auth = getAuth();

	onIdTokenChanged(auth, async (user) => {
		if (user && chatbaseInitialized) {
			console.log("Firebase token refreshed, updating Chatbase...");
			const newToken = await user.getIdToken();
			await updateChatbaseAuth(newToken);
		}
	});
};
